<template>
  <div class="chv-page">
    <Navigation />
    <div class="chv-container">
      <div class="chv-header">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="chv-back"><i class="mdi mdi-arrow-left"></i></a>
        <h4 class="chv-title m-0"><i class="mdi mdi-account-heart me-2"></i>CHV Companion</h4>
        <button class="chv-icon-btn" @click="load"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- Sub-tabs -->
      <div class="chv-tabs">
        <button class="chv-tab" :class="{ active: tab==='queue' }" @click="tab='queue'">
          <i class="mdi mdi-clipboard-list"></i> Defaulters
          <span v-if="totalQueue" class="chv-badge">{{ totalQueue }}</span>
        </button>
        <button class="chv-tab" :class="{ active: tab==='visits' }" @click="tab='visits'; loadVisits()">
          <i class="mdi mdi-home-heart"></i> My visits
        </button>
        <button class="chv-tab" :class="{ active: tab==='referrals' }" @click="tab='referrals'; loadReferrals()">
          <i class="mdi mdi-ambulance"></i> Referrals
        </button>
      </div>

      <!-- ============ DEFAULTERS QUEUE ============ -->
      <div v-if="tab==='queue'">
        <div v-if="!totalQueue" class="chv-empty">
          <i class="mdi mdi-check-circle"></i>
          <p>No defaulters in your queue. Nice work.</p>
        </div>

        <div v-if="queue.alerts && queue.alerts.length" class="chv-section">
          <h6 class="chv-section-title chv-section-critical"><i class="mdi mdi-alert"></i> Open critical alerts ({{ queue.alerts.length }})</h6>
          <div v-for="a in queue.alerts" :key="'al-'+a.trigger_record_id" class="chv-card" :class="'chv-card-' + (a.detail.includes('critical') ? 'red' : 'orange')">
            <div class="chv-card-head">
              <strong>{{ a.first_name }} {{ a.last_name }}</strong>
              <span class="chv-card-tag chv-card-tag-red">{{ a.detail.replace(/_/g,' ') }}</span>
            </div>
            <div class="chv-card-meta">
              <a v-if="a.phone" :href="'tel:' + a.phone"><i class="mdi mdi-phone"></i> {{ a.phone }}</a>
              <span class="chv-muted">since {{ formatRel(a.due_at) }}</span>
            </div>
            <div class="chv-card-actions">
              <button class="chv-btn-primary" @click="startVisit(a, 'mpdsr_action_followup')"><i class="mdi mdi-home"></i> Home visit</button>
              <button class="chv-btn-emergency" @click="startReferral(a)"><i class="mdi mdi-ambulance"></i> SBAR</button>
            </div>
          </div>
        </div>

        <div v-if="queue.pnc && queue.pnc.length" class="chv-section">
          <h6 class="chv-section-title"><i class="mdi mdi-baby-face-outline"></i> PNC defaulters ({{ queue.pnc.length }})</h6>
          <div v-for="p in queue.pnc" :key="'pnc-'+p.trigger_record_id" class="chv-card">
            <div class="chv-card-head">
              <strong>{{ p.first_name }} {{ p.last_name }}</strong>
              <span class="chv-card-tag">{{ p.detail }} PNC missed</span>
            </div>
            <div class="chv-card-meta">
              <a v-if="p.phone" :href="'tel:' + p.phone"><i class="mdi mdi-phone"></i> {{ p.phone }}</a>
              <span class="chv-muted">due {{ formatDate(p.due_at) }}</span>
            </div>
            <div class="chv-card-actions">
              <button class="chv-btn-primary" @click="startVisit(p, 'pnc_defaulter')"><i class="mdi mdi-home"></i> Home visit</button>
            </div>
          </div>
        </div>

        <div v-if="queue.pmtct && queue.pmtct.length" class="chv-section">
          <h6 class="chv-section-title"><i class="mdi mdi-virus"></i> PMTCT VL overdue ({{ queue.pmtct.length }})</h6>
          <div v-for="p in queue.pmtct" :key="'vl-'+p.trigger_record_id" class="chv-card chv-card-orange">
            <div class="chv-card-head">
              <strong>{{ p.first_name }} {{ p.last_name }}</strong>
              <span class="chv-card-tag chv-card-tag-orange">VL {{ p.detail.replace(/_/g,' ') }}</span>
            </div>
            <div class="chv-card-meta">
              <a v-if="p.phone" :href="'tel:' + p.phone"><i class="mdi mdi-phone"></i> {{ p.phone }}</a>
              <span class="chv-muted">due {{ formatDate(p.due_at) }}</span>
            </div>
            <div class="chv-card-actions">
              <button class="chv-btn-primary" @click="startVisit(p, 'pmtct_vl_overdue')"><i class="mdi mdi-home"></i> Home visit</button>
            </div>
          </div>
        </div>

        <div v-if="queue.hei && queue.hei.length" class="chv-section">
          <h6 class="chv-section-title"><i class="mdi mdi-baby-face"></i> Infant DBS overdue ({{ queue.hei.length }})</h6>
          <div v-for="p in queue.hei" :key="'hei-'+p.trigger_record_id" class="chv-card chv-card-orange">
            <div class="chv-card-head">
              <strong>{{ p.first_name }} {{ p.last_name }}</strong>
              <span class="chv-card-tag chv-card-tag-orange">Infant {{ p.detail }} overdue</span>
            </div>
            <div class="chv-card-meta">
              <a v-if="p.phone" :href="'tel:' + p.phone"><i class="mdi mdi-phone"></i> {{ p.phone }}</a>
              <span class="chv-muted">due {{ formatDate(p.due_at) }}</span>
            </div>
            <div class="chv-card-actions">
              <button class="chv-btn-primary" @click="startVisit(p, 'hei_dbs_overdue')"><i class="mdi mdi-home"></i> Home visit</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ MY VISITS ============ -->
      <div v-if="tab==='visits'">
        <div v-if="!visits.length" class="chv-empty">
          <i class="mdi mdi-home-search"></i>
          <p>No visits recorded yet.</p>
        </div>
        <div v-for="v in visits" :key="v.visit_id" class="chv-card">
          <div class="chv-card-head">
            <strong>{{ v.patient_name }}</strong>
            <span class="chv-card-tag" :class="outcomeTagClass(v.outcome)">{{ v.outcome.replace(/_/g,' ') }}</span>
          </div>
          <div class="chv-card-meta">
            <span><i class="mdi mdi-clock-outline"></i> {{ formatDateTime(v.visit_date) }}</span>
            <span class="chv-muted">{{ (v.trigger_type || '').replace(/_/g,' ') }}</span>
          </div>
          <div v-if="v.danger_signs_observed && v.danger_signs_observed !== 'none'" class="chv-card-warn">
            <i class="mdi mdi-alert"></i> {{ v.danger_signs_observed.replace(/,/g, ' · ') }}
          </div>
          <div v-if="v.notes" class="chv-card-notes">{{ v.notes }}</div>
        </div>
      </div>

      <!-- ============ REFERRALS ============ -->
      <div v-if="tab==='referrals'">
        <div v-if="!referrals.length" class="chv-empty">
          <i class="mdi mdi-ambulance"></i>
          <p>No referrals issued.</p>
        </div>
        <div v-for="r in referrals" :key="r.referral_id" class="chv-card" :class="'chv-card-' + urgencyColor(r.urgency)">
          <div class="chv-card-head">
            <strong>{{ r.patient_name }}</strong>
            <span class="chv-card-tag" :class="urgencyTag(r.urgency)">{{ r.urgency.replace(/_/g,' ') }}</span>
          </div>
          <div class="chv-card-meta">
            <span><i class="mdi mdi-clock-outline"></i> {{ formatDateTime(r.referred_at) }}</span>
            <span class="chv-muted">→ {{ r.referral_facility || 'TBD' }}</span>
          </div>
          <div class="chv-sbar">
            <div><strong>S:</strong> {{ r.sbar_situation }}</div>
            <div v-if="r.sbar_background"><strong>B:</strong> {{ r.sbar_background }}</div>
            <div v-if="r.sbar_assessment"><strong>A:</strong> {{ r.sbar_assessment }}</div>
            <div v-if="r.sbar_recommendation"><strong>R:</strong> {{ r.sbar_recommendation }}</div>
          </div>
          <div class="chv-card-meta">
            <span class="chv-pill" :class="refStatusPill(r.status)">{{ r.status.replace(/_/g,' ') }}</span>
            <span v-if="r.transport_arranged==1" class="chv-pill chv-pill-info">{{ r.transport_mode || 'transport' }}</span>
          </div>
        </div>
      </div>

      <!-- ============ HOME VISIT MODAL ============ -->
      <div v-if="visitModal" class="chv-modal-bg" @click.self="visitModal=false">
        <div class="chv-modal">
          <div class="chv-modal-head">
            <h5><i class="mdi mdi-home-heart"></i> Home visit — {{ activeTarget?.first_name }} {{ activeTarget?.last_name }}</h5>
            <button class="chv-icon-btn" @click="visitModal=false"><i class="mdi mdi-close"></i></button>
          </div>
          <div class="chv-modal-body">
            <div class="chv-form-group">
              <label>Contact made</label>
              <select v-model="visitForm.contact_made" class="chv-input">
                <option value="mother_seen">Mother seen at home</option>
                <option value="partner_or_relative_only">Only partner/relative seen</option>
                <option value="phone_only">Reached by phone only</option>
                <option value="no_contact">No contact</option>
                <option value="refused">Mother refused engagement</option>
              </select>
            </div>

            <div class="chv-form-group">
              <label>Outcome</label>
              <select v-model="visitForm.outcome" class="chv-input">
                <option value="returned_to_care">Returned to care (appointment honoured)</option>
                <option value="appointment_booked">Appointment booked</option>
                <option value="referred">Emergency referral made</option>
                <option value="still_defaulting">Still defaulting — re-visit needed</option>
                <option value="transferred">Has transferred to another facility</option>
                <option value="died">Died (open death record)</option>
                <option value="LTFU_confirmed">LTFU confirmed</option>
              </select>
            </div>

            <div class="chv-form-group">
              <label>Appointment booked date</label>
              <input type="date" v-model="visitForm.appointment_booked_date" class="chv-input" />
            </div>

            <!-- Maternal check -->
            <h6 class="chv-section-title"><i class="mdi mdi-account-heart"></i> Mother</h6>
            <div class="chv-form-row">
              <label class="chv-check"><input type="checkbox" v-model="visitForm.mother_well" :true-value="1" :false-value="0" /> Mother appears well</label>
            </div>
            <div class="chv-form-group">
              <label>Concerns reported</label>
              <input v-model="visitForm.mother_concerns" class="chv-input" placeholder="What the mother says" />
            </div>
            <div class="chv-form-group">
              <label>Maternal danger signs observed (any → critical alert)</label>
              <div class="chv-chip-row">
                <label v-for="opt in maternalDangerOpts" :key="opt" class="chv-chip">
                  <input type="checkbox" :value="opt" v-model="motherDangerArr" /> {{ opt.replace(/_/g,' ') }}
                </label>
              </div>
            </div>

            <!-- Baby check (only if PNC/HEI trigger) -->
            <div v-if="['pnc_defaulter','hei_dbs_overdue'].includes(activeTrigger)">
              <h6 class="chv-section-title"><i class="mdi mdi-baby-face-outline"></i> Baby</h6>
              <div class="chv-form-row">
                <label class="chv-check"><input type="checkbox" v-model="visitForm.baby_well" :true-value="1" :false-value="0" /> Baby appears well</label>
              </div>
              <div class="chv-form-group">
                <label>Feeding</label>
                <input v-model="visitForm.baby_feeding" class="chv-input" placeholder="EBF / mixed / formula" />
              </div>
              <div class="chv-form-group">
                <label>Baby danger signs</label>
                <div class="chv-chip-row">
                  <label v-for="opt in babyDangerOpts" :key="opt" class="chv-chip">
                    <input type="checkbox" :value="opt" v-model="babyDangerArr" /> {{ opt.replace(/_/g,' ') }}
                  </label>
                </div>
              </div>
            </div>

            <!-- ART adherence (PMTCT) -->
            <div v-if="activeTrigger==='pmtct_vl_overdue'">
              <h6 class="chv-section-title"><i class="mdi mdi-pill"></i> ART adherence</h6>
              <div class="chv-form-row">
                <label class="chv-check"><input type="checkbox" v-model="visitForm.art_taken_yesterday" :true-value="1" :false-value="0" /> Took ART yesterday</label>
              </div>
              <div class="chv-form-group">
                <label>Doses remaining in bottle</label>
                <input type="number" v-model="visitForm.art_remaining_doses" class="chv-input" />
              </div>
            </div>

            <div class="chv-form-group">
              <label>Travel distance (km)</label>
              <input type="number" step="0.1" v-model="visitForm.travel_distance_km" class="chv-input" />
            </div>
            <div class="chv-form-group">
              <label>Notes</label>
              <textarea v-model="visitForm.notes" rows="3" class="chv-input"></textarea>
            </div>
          </div>
          <div class="chv-modal-foot">
            <button class="chv-btn-secondary" @click="visitModal=false">Cancel</button>
            <button class="chv-btn-primary" :disabled="saving" @click="saveVisit">{{ saving ? 'Saving…' : 'Save visit' }}</button>
          </div>
        </div>
      </div>

      <!-- ============ SBAR REFERRAL MODAL ============ -->
      <div v-if="referralModal" class="chv-modal-bg" @click.self="referralModal=false">
        <div class="chv-modal">
          <div class="chv-modal-head">
            <h5><i class="mdi mdi-ambulance"></i> SBAR referral — {{ activeTarget?.first_name }} {{ activeTarget?.last_name }}</h5>
            <button class="chv-icon-btn" @click="referralModal=false"><i class="mdi mdi-close"></i></button>
          </div>
          <div class="chv-modal-body">
            <div class="chv-form-group">
              <label>Urgency</label>
              <select v-model="refForm.urgency" class="chv-input">
                <option value="emergency_immediate">Emergency — go NOW</option>
                <option value="urgent_4hr">Urgent — within 4 hours</option>
                <option value="within_24h">Within 24 hours</option>
                <option value="routine">Routine</option>
              </select>
            </div>
            <div class="chv-form-group">
              <label>Refer to (facility)</label>
              <input v-model="refForm.referral_facility" class="chv-input" placeholder="e.g. Narok County Referral" />
            </div>
            <div class="chv-form-group">
              <label>Receiving provider</label>
              <input v-model="refForm.receiving_provider" class="chv-input" placeholder="Name of nurse/doctor on call" />
            </div>

            <div class="chv-sbar-form">
              <div class="chv-form-group">
                <label><strong>S</strong>ituation — what is happening NOW</label>
                <textarea v-model="refForm.sbar_situation" rows="2" class="chv-input" placeholder="e.g. 28-yr postpartum mother day 3, heavy bleeding, soaking 2 pads/hr, looks pale"></textarea>
              </div>
              <div class="chv-form-group">
                <label><strong>B</strong>ackground — relevant history</label>
                <textarea v-model="refForm.sbar_background" rows="2" class="chv-input" placeholder="e.g. SVD at home 3 days ago, G3P3, no ANC; CHV first contact"></textarea>
              </div>
              <div class="chv-form-group">
                <label><strong>A</strong>ssessment — your reading</label>
                <textarea v-model="refForm.sbar_assessment" rows="2" class="chv-input" placeholder="e.g. Suspected secondary PPH ± anaemia; shock-risk"></textarea>
              </div>
              <div class="chv-form-group">
                <label><strong>R</strong>ecommendation — what's needed</label>
                <textarea v-model="refForm.sbar_recommendation" rows="2" class="chv-input" placeholder="e.g. Immediate transfer, IV line en-route, type/cross-match ready"></textarea>
              </div>
            </div>

            <div class="chv-form-row">
              <label class="chv-check"><input type="checkbox" v-model="refForm.receiver_called" :true-value="1" :false-value="0" /> Receiver called and accepted</label>
            </div>
            <div class="chv-form-row">
              <label class="chv-check"><input type="checkbox" v-model="refForm.transport_arranged" :true-value="1" :false-value="0" /> Transport arranged</label>
              <input v-model="refForm.transport_mode" class="chv-input" placeholder="Ambulance / boda / private" style="margin-left:8px" />
            </div>
          </div>
          <div class="chv-modal-foot">
            <button class="chv-btn-secondary" @click="referralModal=false">Cancel</button>
            <button class="chv-btn-emergency" :disabled="!refForm.sbar_situation || saving" @click="saveReferral">{{ saving ? 'Submitting…' : 'Submit referral' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "ChvCompanion",
  components: { Navigation },
  data() {
    return {
      tab: 'queue', saving: false,
      queue: { pnc: [], pmtct: [], hei: [], alerts: [], total: 0 },
      visits: [], referrals: [],
      visitModal: false, referralModal: false,
      activeTarget: null, activeTrigger: '',
      visitForm: this.blankVisitForm(),
      refForm: this.blankRefForm(),
      motherDangerArr: [], babyDangerArr: [],
      maternalDangerOpts: ['heavy_bleeding','fever','headache','swelling','difficulty_breathing','convulsions','severe_pain','depression'],
      babyDangerOpts: ['not_feeding','fast_breathing','fever','hypothermia','jaundice','lethargy']
    };
  },
  computed: {
    totalQueue() { return this.queue.total || 0; }
  },
  mounted() { this.load(); },
  methods: {
    blankVisitForm() { return { patient_id: null, trigger_type: 'routine', trigger_record_id: null,
      contact_made: 'mother_seen', outcome: 'returned_to_care', appointment_booked_date: '',
      mother_well: 1, mother_concerns: '', baby_well: 1, baby_feeding: '',
      art_taken_yesterday: 1, art_remaining_doses: null,
      travel_distance_km: null, notes: '' }; },
    blankRefForm() { return { patient_id: null, urgency: 'urgent_4hr',
      referral_facility: '', receiving_provider: '',
      sbar_situation: '', sbar_background: '', sbar_assessment: '', sbar_recommendation: '',
      receiver_called: 0, transport_arranged: 0, transport_mode: '' }; },
    async load() {
      try {
        const r = await executeJson({ function: 'getChvDefaulters' }, hms);
        if (r.data && r.data.success) this.queue = r.data.data || { pnc: [], pmtct: [], hei: [], alerts: [], total: 0 };
      } catch (e) { console.error(e); }
    },
    async loadVisits() {
      const r = await executeJson({ function: 'getChvMyVisits' }, hms);
      if (r.data && r.data.success) this.visits = r.data.data.rows || [];
    },
    async loadReferrals() {
      const r = await executeJson({ function: 'getSbarReferrals' }, hms);
      if (r.data && r.data.success) this.referrals = r.data.data.rows || [];
    },
    startVisit(target, trigger) {
      this.activeTarget = target; this.activeTrigger = trigger;
      this.visitForm = this.blankVisitForm();
      this.visitForm.patient_id = target.patient_id;
      this.visitForm.trigger_type = trigger;
      this.visitForm.trigger_record_id = target.trigger_record_id;
      this.motherDangerArr = []; this.babyDangerArr = [];
      this.visitModal = true;
    },
    async saveVisit() {
      this.saving = true;
      try {
        const payload = { function: 'recordChvVisit', ...this.visitForm,
          danger_signs_observed: this.motherDangerArr.length ? this.motherDangerArr.join(',') : 'none',
          baby_danger_signs: this.babyDangerArr.length ? this.babyDangerArr.join(',') : 'none' };
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) { this.visitModal = false; await this.load(); }
        else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    startReferral(target) {
      this.activeTarget = target;
      this.refForm = this.blankRefForm();
      this.refForm.patient_id = target.patient_id;
      this.refForm.urgency = 'emergency_immediate';
      this.referralModal = true;
    },
    async saveReferral() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'submitSbarReferral', ...this.refForm }, hms);
        if (r.data && r.data.success) { this.referralModal = false; alert('Referral submitted'); }
        else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    formatDate(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleDateString(); },
    formatDateTime(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleString(); },
    formatRel(d) {
      if (!d) return '';
      const dt = new Date((d + '').replace(' ','T'));
      const hrs = Math.floor((Date.now() - dt.getTime()) / 3600000);
      if (hrs < 1) return 'just now';
      if (hrs < 24) return hrs + 'h ago';
      return Math.floor(hrs / 24) + 'd ago';
    },
    outcomeTagClass(o) { return ({ returned_to_care: 'chv-card-tag-green', appointment_booked: 'chv-card-tag-blue', referred: 'chv-card-tag-orange', still_defaulting: 'chv-card-tag-red', died: 'chv-card-tag-red', LTFU_confirmed: 'chv-card-tag-red' })[o] || ''; },
    urgencyColor(u) { return ({ emergency_immediate: 'red', urgent_4hr: 'orange' })[u] || ''; },
    urgencyTag(u) { return ({ emergency_immediate: 'chv-card-tag-red', urgent_4hr: 'chv-card-tag-orange', within_24h: 'chv-card-tag-blue' })[u] || ''; },
    refStatusPill(s) { return ({ initiated: 'chv-pill-warn', accepted: 'chv-pill-info', arrived: 'chv-pill-success', admitted: 'chv-pill-success', rejected: 'chv-pill-danger', no_show: 'chv-pill-danger' })[s] || 'chv-pill-muted'; }
  }
};
</script>

<style scoped>
.chv-page { background: #f7fafc; min-height: 100vh; }
.chv-container { max-width: 560px; margin: 0 auto; padding: 12px; padding-bottom: 60px; }

.chv-header { display: flex; align-items: center; gap: 12px; padding: 10px 0 14px; }
.chv-back { color: #4a5568; font-size: 22px; text-decoration: none; }
.chv-title { flex: 1; font-size: 18px; font-weight: 600; color: #2d3748; }
.chv-icon-btn { background: transparent; border: none; color: #4a5568; font-size: 18px; cursor: pointer; padding: 4px; }

.chv-tabs { display: flex; background: #fff; border-radius: 8px; padding: 4px; margin-bottom: 14px; gap: 4px; border: 1px solid #e2e8f0; }
.chv-tab { flex: 1; background: transparent; border: none; padding: 10px 8px; font-size: 13px; font-weight: 500; color: #718096; border-radius: 6px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 4px; }
.chv-tab.active { background: #2b6cb0; color: #fff; }
.chv-badge { background: #dc2626; color: #fff; font-size: 10px; padding: 1px 6px; border-radius: 10px; margin-left: 4px; }

.chv-section { margin-bottom: 16px; }
.chv-section-title { font-size: 13px; font-weight: 600; color: #2d3748; margin: 14px 4px 8px; }
.chv-section-critical { color: #b91c1c; }

.chv-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; margin-bottom: 10px; border-left: 4px solid #cbd5e0; }
.chv-card-red    { border-left-color: #dc2626; }
.chv-card-orange { border-left-color: #ea580c; }

.chv-card-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.chv-card-head strong { font-size: 15px; color: #2d3748; }
.chv-card-tag { background: #edf2f7; color: #4a5568; font-size: 10px; padding: 3px 8px; border-radius: 12px; font-weight: 600; text-transform: capitalize; }
.chv-card-tag-red    { background: #fed7d7; color: #742a2a; }
.chv-card-tag-orange { background: #feebc8; color: #7b341e; }
.chv-card-tag-blue   { background: #bee3f8; color: #2a4365; }
.chv-card-tag-green  { background: #c6f6d5; color: #22543d; }

.chv-card-meta { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; color: #4a5568; margin: 4px 0; flex-wrap: wrap; }
.chv-card-meta a { color: #2b6cb0; text-decoration: none; font-weight: 600; }
.chv-card-meta a:hover { text-decoration: underline; }

.chv-muted { color: #a0aec0; }

.chv-card-warn { background: #fef2f2; color: #7f1d1d; padding: 6px 10px; border-radius: 4px; font-size: 12px; margin: 6px 0; }
.chv-card-notes { font-size: 12px; color: #4a5568; padding: 6px 0; border-top: 1px solid #edf2f7; margin-top: 6px; }

.chv-sbar { background: #f7fafc; padding: 8px 10px; border-radius: 6px; font-size: 12px; line-height: 1.6; color: #2d3748; margin: 6px 0; }

.chv-card-actions { display: flex; gap: 8px; margin-top: 8px; }

.chv-btn-primary, .chv-btn-emergency, .chv-btn-secondary {
  flex: 1; padding: 10px 12px; font-size: 14px; font-weight: 600;
  border: none; border-radius: 6px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 4px;
}
.chv-btn-primary   { background: #2b6cb0; color: #fff; }
.chv-btn-emergency { background: #dc2626; color: #fff; }
.chv-btn-secondary { background: #edf2f7; color: #2d3748; }

.chv-empty { text-align: center; padding: 60px 20px; color: #a0aec0; }
.chv-empty i { font-size: 48px; }
.chv-empty p { font-size: 14px; margin-top: 8px; }

.chv-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.chv-pill-success { background: #c6f6d5; color: #22543d; }
.chv-pill-info    { background: #bee3f8; color: #2a4365; }
.chv-pill-warn    { background: #feebc8; color: #7b341e; }
.chv-pill-danger  { background: #fed7d7; color: #742a2a; }
.chv-pill-muted   { background: #edf2f7; color: #4a5568; }

/* Modals (mobile-first) */
.chv-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: flex-end; justify-content: center; z-index: 100; }
.chv-modal { background: #fff; border-radius: 12px 12px 0 0; width: 100%; max-width: 560px; max-height: 92vh; display: flex; flex-direction: column; }
@media (min-width: 600px) { .chv-modal-bg { align-items: center; padding: 20px; } .chv-modal { border-radius: 12px; } }
.chv-modal-head { padding: 14px 16px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.chv-modal-head h5 { margin: 0; font-size: 15px; font-weight: 600; }
.chv-modal-body { padding: 14px 16px; overflow-y: auto; }
.chv-modal-foot { padding: 12px 16px; border-top: 1px solid #e2e8f0; display: flex; gap: 8px; }

.chv-form-group { margin-bottom: 12px; }
.chv-form-group label { display: block; font-size: 12px; font-weight: 600; color: #4a5568; margin-bottom: 4px; }
.chv-form-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.chv-input { width: 100%; padding: 9px 12px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 14px; }
.chv-check { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; }

.chv-chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.chv-chip { display: inline-flex; align-items: center; gap: 4px; background: #f7fafc; border: 1px solid #e2e8f0; padding: 6px 10px; border-radius: 14px; font-size: 12px; cursor: pointer; }
.chv-chip:has(input:checked) { background: #fed7d7; border-color: #fc8181; color: #742a2a; }

.chv-sbar-form { background: #f7fafc; padding: 10px; border-radius: 6px; margin: 10px 0; }
</style>
